import React, { useContext } from 'react';
import { VersionContext } from '@modules/test-development/contexts/version.context';
import useDebuggerRunButton from '@modules/test-development/components/cases/ad-hoc-runs/use-debugger-run-button.hook';

import DebuggerStopStartModal from './debugger-start-stop-modal';

interface WithStartStopModalProps {
  refresh?: () => void;
}

interface StartStopModalInjectedProps {
  // All useDebuggerRunButton hook properties (always provided with defaults)
  isChecking: boolean;
  showDebugRunForm: boolean;
  isDebuggerAvailable: boolean;
  setShowDebugRunForm: (show: boolean) => void;
  isAlreadyDebuggerSessionFound: any;
  systemInfo: any;
  handleClick: () => void;
  closeAlreadyDebuggerSessionFound: () => void;
  stopExecution: (refresh?: () => void) => void;
  isStopping: boolean;
  testCaseResult: any;
  agentInfo: any;
  stopOnly: boolean;
  checkRunningDebuggerSession: (session: any) => void;
}

/**
 * HOC that provides DebuggerStopStartModal functionality
 * Handles the useDebuggerRunButton hook and modal logic
 */
export function withStartStopModal<P extends object>(
  Component: React.ComponentType<P & StartStopModalInjectedProps>,
): React.FC<P & WithStartStopModalProps> {
  return function WrappedWithStartStopModal(
    props: P & WithStartStopModalProps,
  ) {
    const { refresh, ...componentProps } = props;
    const { version } = useContext(VersionContext);

    const debuggerHookResult = useDebuggerRunButton(version);

    const {
      isAlreadyDebuggerSessionFound,
      closeAlreadyDebuggerSessionFound,
      testCaseResult,
      stopExecution,
      isStopping,
      agentInfo,
      stopOnly,
    } = debuggerHookResult;

    // Handle stop execution with optional refresh callback
    const handleStop = refresh ? () => stopExecution(refresh) : stopExecution;

    // Inject all debugger session functionality with default values
    const enhancedProps = {
      ...componentProps,
      // Pass refresh prop to the wrapped component
      refresh,
      // Provide safe defaults for all debugger hook properties
      isChecking: false,
      showDebugRunForm: false,
      isDebuggerAvailable: false,
      setShowDebugRunForm: () => {},
      isAlreadyDebuggerSessionFound: null,
      systemInfo: null,
      handleClick: () => {},
      closeAlreadyDebuggerSessionFound: () => {},
      stopExecution: () => {},
      isStopping: false,
      testCaseResult: null,
      agentInfo: null,
      stopOnly: false,
      checkRunningDebuggerSession: () => {},
      // Override with actual values from hook
      ...debuggerHookResult,
    } as unknown as P & StartStopModalInjectedProps;

    return (
      <>
        <Component {...enhancedProps} />
        <DebuggerStopStartModal
          show={!!isAlreadyDebuggerSessionFound}
          onClose={closeAlreadyDebuggerSessionFound}
          sessionDetails={isAlreadyDebuggerSessionFound}
          testCaseResult={testCaseResult}
          agent={agentInfo}
          onStop={handleStop}
          isStopping={isStopping}
          agent={agentInfo}
          stopOnly={stopOnly}
        />
      </>
    );
  };
}

export default withStartStopModal;
